import { Badge } from "@workspace/web-ui/components/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs"
import { Button, buttonVariants } from "@workspace/web-ui/components/button"
import SubNav from "../../components/sub-nav"
// import AppointmentUi from "./components/appointment-ui"
import EmptyAppointment from "./components/empty-appointment"
import { useSuspenseQuery,useQuery } from "@tanstack/react-query"
import { appointmentsQueryOptions,appointmentQueryOptions } from "./api/queries"
import { linkOptions, useNavigate, useParams } from "@tanstack/react-router"
import { CalendarMinus2, Clock, Calendar, Video, X, CircleCheck, Info, CircleX, UserRound, Bell, CalendarOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react'
import { useState,useEffect } from "react"
import { cn } from "@workspace/web-ui/lib/utils"
import { Appointment } from "~/features/appointments/types"
import { formatEncounterAppointmentStatusTag } from "@workspace/lib/date"
 import { Skeleton } from "@workspace/web-ui/components/skeleton"
 import { useAuth } from "~/providers/auth.provider";
 
export const appointmentsLinkOptions = (tab = 'upcoming') => linkOptions({
    to: '/dashboard/weight-loss/appointments/$tab',
    params: { tab }
})

const getStatusConfig = (appointment: Appointment) => {
    let { status, tag } = formatEncounterAppointmentStatusTag({
        date: appointment.date,
        time: appointment.time,
        status: appointment.status as "scheduled" | "attended" | "cancelled",
        meetingInterval: appointment.time_interval_minutes
    })

    if (appointment.status === "attended" && appointment.encounter?.recommended_tasks?.some(task => task.status === "pending")) {
        status = "actionNeeded"
        tag = "Action Needed"
    }

    const statusMap = {
        "happening": { color: "bg-[#EBF0FB] text-[#4272DD]", icon: <Video size={14} />, text: tag, status: "happening" },
        "upcoming": { color: "bg-[#FFF6E7] text-[#FFAB1F]", icon: <Calendar size={14} />, text: tag, status: "upcoming" },
        "past": { color: "bg-[#F1F1F1] text-[#7F8493]", icon: <CalendarOff size={14} />, text: tag, status: "Missed" },
        "cancelled": { color: "bg-[#F1F1F1] text-[#7F8493]", icon: <CalendarOff size={14} />, text: tag, status: "cancelled" },
        "completed": { color: "bg-[#EBFBF1] text-[#14AD4D]", icon: <CheckCircle size={14} />, text: tag, status: "completed" },
        "actionNeeded": { color: "bg-[#EBF0FB] text-[#4272DD]", icon: <AlertCircle size={14} />, text: tag, status: "actionNeeded" },
    };

    return statusMap[status as keyof typeof statusMap];
};

export function AppointmentTabPage() {
    const { tab } = useParams({ strict: false })
    const navigate = useNavigate()
  const { user } = useAuth();

    const appointmentsQuery = useSuspenseQuery(appointmentsQueryOptions({ type: "upcoming" }))
    const appointmentsPastQuery = useSuspenseQuery(appointmentsQueryOptions({ type: "past" }))
     return (
        <main className="flex flex-col gap-6">
            <section className="py-10 gap-y-4">
                <SubNav
                    title="Appointments"
                    buttonOne="Go to urgent care"
                    buttonTwo="Book an appointment"
                    disable={user?.has_plan !== true} // Disable button if user does not have a plan
                />
                <AppointmentReschedule />

                {/* Timeline */}
                <div className="space-y-8 ">
                    <Tabs defaultValue="upcoming" value={tab} onValueChange={(tab) => navigate(appointmentsLinkOptions(tab))}>
                        <TabsList className="h-12 w-full justify-start gap-6 rounded-none border-b mb-3 py-0">
                            <TabsTrigger
                                value="upcoming"
                                className="h-full rounded-none border-b-2 border-transparent  px-2 py-4 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                            >
                                Upcoming
                                <Badge className="ml-2 size-4 rounded-full bg-[#FFAB1F] p-2 justify-center items-center text-center text-xs">
                                    {appointmentsQuery.data?.appointments?.length || 0}
                                </Badge>
                            </TabsTrigger>
                            <TabsTrigger
                                value="past"
                                className="h-full rounded-none border-b-2 border-transparent px-0 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                            >
                                Past
                                <Badge className="ml-2 size-4 rounded-full bg-[#FFAB1F] p-2 justify-center items-center text-center text-xs">
                                    {appointmentsPastQuery.data?.appointments?.length || 0}
                                </Badge>
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="upcoming" className="flex gap-4 w-full">
                            {appointmentsQuery.data?.appointments && appointmentsQuery.data?.appointments?.length > 0 ? 
                                <UpcomingAppointments appointments={appointmentsQuery.data?.appointments} /> : 
                                <EmptyAppointment timeline="upcoming" />
                            }
                        </TabsContent>

                        <TabsContent value="past" className="flex gap-4">
                            {appointmentsPastQuery.data?.appointments && appointmentsPastQuery.data?.appointments?.length > 0 ? 
                                <PastAppointments appointments={appointmentsPastQuery.data?.appointments} /> : 
                                <EmptyAppointment timeline="past" />
                            }
                        </TabsContent>
                    </Tabs>
                </div>
            </section>
        </main>
    )
}

const UpcomingAppointments = ({ appointments }: { appointments: any[] }) => {
    const [selectedAppointment, setSelectedAppointment] = useState(appointments[0]?.appointment_id || null)
    const appointmentQuery = useQuery(appointmentQueryOptions(selectedAppointment))
    const [showDetails, setShowDetails] = useState(false)

    const handleAppointmentSelect = (id:string) => {
        setSelectedAppointment(id)
        setShowDetails(true)
        appointmentQuery.refetch()
    }

    const handleBack = () => {
        setShowDetails(false)
    }

    useEffect(() => {   
        if(selectedAppointment !== '' && selectedAppointment !== undefined) {
            appointmentQuery.refetch()
            console.log("Selected appointment:", appointmentQuery.data);
        }
    }, [selectedAppointment])
      
    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="w-full grid lg:grid-cols-2 grid-cols-1 gap-6">
                <div className={`flex flex-col gap-[16px] w-full ${showDetails ? 'hidden lg:flex' : 'flex'}`}>
                    {appointments.map((appointment, index) => (
                        <AppointmentsCard 
                            key={appointment.appointment_id || index} 
                            appointment={appointment}
                            isSelected={selectedAppointment?.appointment_id === appointment.appointment_id}
                            onClick={() => handleAppointmentSelect(appointment.appointment_id)}
                        />
                    ))}
                </div>
                <div className={`${showDetails ? 'block' : 'hidden lg:block'} w-full`}>
                    {showDetails && (
                        <div className="lg:hidden mb-4">
                            <Button 
                                variant="ghost" 
                                onClick={handleBack}
                                className="flex items-center gap-2 text-[#464B55]"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to appointments
                            </Button>
                        </div>
                    )}
                    <AppointmentsDetails isLoading={appointmentQuery?.isLoading} appointment={appointmentQuery?.data?.appointment} />
                    </div>
            </div>
        </div>
    )
}

const PastAppointments = ({ appointments }: { appointments: any[] }) => {
    const [selectedAppointment, setSelectedAppointment] = useState(appointments[0]?.appointment_id)
    const appointmentQuery = useQuery(appointmentQueryOptions(selectedAppointment))
    const [showDetails, setShowDetails] = useState(false)

    const handleAppointmentSelect = (id:string) => {
        setSelectedAppointment(id)
        setShowDetails(true)
        appointmentQuery.refetch()
    }

    const handleBack = () => {
        setShowDetails(false)
    }

    useEffect(() => {   
        if(selectedAppointment !== '' && selectedAppointment !== undefined) {
            appointmentQuery.refetch()
            console.log("Selected appointment:", appointmentQuery);

        }
    }, [selectedAppointment])

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="w-full grid lg:grid-cols-2 grid-cols-1 gap-6">
                <div className={`flex flex-col gap-[16px] w-full ${showDetails ? 'hidden lg:flex' : 'flex'}`}>
                    {appointments.map((appointment, index) => (
                        <AppointmentsCard 
                            key={appointment?.appointment_id || index} 
                            appointment={appointment}
                            isSelected={selectedAppointment?.appointment_id === appointment?.appointment_id}
                            onClick={() => handleAppointmentSelect(appointment?.appointment_id)}
                        />
                    ))}
                </div>
                <div className={`${showDetails ? 'block' : 'hidden lg:block'} w-full`}>
                    {showDetails && (
                        <div className="lg:hidden mb-4">
                            <Button 
                                variant="ghost" 
                                onClick={handleBack}
                                className="flex items-center gap-2 text-[#464B55]"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to appointments
                            </Button>
                        </div>
                    )}
                    <AppointmentsDetails isLoading={appointmentQuery?.isLoading} appointment={appointmentQuery?.data?.appointment} />
                </div>
            </div>
        </div>
    )
}

const AppointmentsCard = ({ appointment, isSelected, onClick }: { 
    appointment: any, 
    isSelected: boolean, 
    onClick: () => void 
}) => {
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        })
    }

    const formatTime = (timeString: string) => {
        return new Date(timeString).toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
        })
    }

    const statusConfig = getStatusConfig(appointment)

    return (
        <div 
            role="button" 
            onClick={onClick}
            className={`border rounded-[12px] hover:border-[#04040466] lg:p-[20px] p-[16px] flex flex-col gap-[8px] w-full transition-colors ${
                isSelected ? 'border-[#04040466] bg-[#F8F9FA]' : 'border-[#0404041A]'
            }`}
        >
            <div className="flex items-center gap-2">
               <h1 className="text-[#1A202C] text-[18px] font-medium leading-[130%] tracking-[-1%]">
                   {appointment.title || 'Weight Loss Consultation'}
               </h1>
            </div>
            <div className="flex w-full items-center gap-4">
                <div className="flex items-center gap-2 text-[#464B55] text-[14px] leading-[140%] font-normal tracking-[-1%]">
                    <CalendarMinus2 className="w-4 h-4" />
                    {formatDate(appointment.date)}
                </div>
                <div className="flex items-center gap-2 text-[#464B55] text-[14px] leading-[140%] font-normal tracking-[-1%]">
                    <Clock className="w-4 h-4" />
                    {formatTime(appointment.meeting_utc_datetime)}
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-[8px]">
            {/* <div className={`inline-flex rounded-3xl items-center gap-2  px-2 py-1 ${statusConfig.color}`}>
                {statusConfig.icon}
                <span className="text-sm font-medium">{statusConfig.text}</span>
            </div> */}
                <AppointmentStatusTag type={statusConfig.status} />
            </div>
        </div>
    )
}

const AppointmentsDetails = ({ appointment,isLoading }: { appointment: any, isLoading?: boolean }) => {
    const navigate = useNavigate()
    if (!appointment || isLoading) {
        return (
            <div className="border rounded-[12px] border-[#04040466] p-[20px] w-full">
                <p className="text-[#464B55] text-center">Select an appointment to view details</p>
            </div>
        )
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', { 
            weekday: 'long',
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
        })
    }

    const formatTime = (timeString: string) => {
        return new Date(timeString).toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
        })
    }

    const handleSelectAppointment = (id: string) => {
        navigate({ to: `/dashboard/weight-loss/appointments/reschedule-booking?appointment_id=${id}`, }); // Navigate after setting the value to reschedule booking
    };

    const isUpcoming = appointment.status === 'scheduled' || appointment.status === 'confirmed'
    // const isPast = appointment.status === 'completed' || appointment.status === 'missed'

    const statusConfig = getStatusConfig(appointment)

    return (
        <>
        {!isLoading ?
      ( 
         <div className="border rounded-[12px] border-[#04040466] py-[20px] ">
            <div className="flex flex-col  gap-[16px] lg:px-[20px] px-[16px]">
                <div className="flex lg:flex-row flex-col lg:justify-between lg:items-center gap-[8px]">
                    <h1 className="text-[#1A202C] text-[20px] font-medium leading-[130%] tracking-[-2%]">
                        {appointment.title || 'Weight Loss Consultation'}
                    </h1>
                    <div className="w-full lg:w-[45%] flex flex-row lg:justify-end justify-start items-center gap-2"> 
                    <div className={`inline-flex rounded-3xl items-center gap-2  px-2 py-1 ${statusConfig.color}`}>
                {statusConfig.icon}
                <span className="text-sm font-medium">{statusConfig.text}</span>
            </div>
                        {/* <AppointmentTimeTag time={formatTime(appointment.scheduled_at)} /> */}
                    </div>
                </div>
                <div className="flex gap-[12px] flex-col">
                    <div className="flex flex-col justify-between gap-[8px]">
                        <div className="flex flex-row gap-[16px]">
                            <CalendarMinus2 className="w-4 h-4 mt-1 text-[#6C757D]" />
                            <div className="flex flex-col gap-[4px]">
                                <h1 className="text-[#1A202C] text-[14px] leading-[140%] font-medium tracking-[-1%]">Date</h1>                           
                                <span className="text-[#464B55] text-[14px] leading-[140%] font-normal tracking-[-1%]">
                                    {formatDate(appointment.date)}
                                </span>
                            </div>                       
                        </div>
                        <div className="flex flex-row gap-[16px]">
                            <Clock className="w-4 h-4 mt-1 text-[#6C757D]" />
                            <div className="flex flex-col gap-[4px]">
                                <h1 className="text-[#1A202C] text-[14px] leading-[140%] font-medium tracking-[-1%]">Time</h1>                           
                                <span className="text-[#464B55] text-[14px] leading-[140%] font-normal tracking-[-1%]">
                                    {formatTime(appointment.meeting_utc_datetime)}
                                </span>
                            </div>                       
                        </div>
                        <div className="flex flex-row gap-[16px]">
                            <Video className="w-4 h-4 mt-1 text-[#6C757D]" />
                            <div className="flex flex-col gap-[4px]">
                                <h1 className="text-[#1A202C] text-[14px] leading-[140%] font-medium tracking-[-1%]">Location</h1>                           
                                <span className="text-[#464B55] text-[14px] leading-[140%] font-normal tracking-[-1%]">
                                    {appointment.location || "Zoom"}
                                </span>
                            </div>                       
                        </div>
                    </div>
                </div>

                {/* Status indicator */}
                {appointment.status === 'completed' && (
                    <div className="flex flex-row gap-[8px] items-center bg-[#E6FAF2] rounded-[8px] p-[12px]">
                        <Info className="w-4 h-4 text-[#00C781]" /> 
                        <span className="text-[#003D26] text-[14px] leading-[140%] font-medium tracking-[-1%]">
                            You attended this appointment
                        </span>
                    </div>
                )}

                {statusConfig.status === 'Missed' && (
                    <div className="flex flex-row gap-[8px] items-center bg-[#F1F4F9] rounded-[8px] p-[12px]">
                        <Info className="w-4 h-4 text-[#FFA726]" /> 
                        <span className="text-[#464B55] text-[14px] leading-[140%] font-medium tracking-[-1%]">
                            You missed this appointment
                        </span>
                    </div>
                )}

                {/* Recommended tasks for upcoming appointments */}
                {/* {isUpcoming && (
                    <div className="flex flex-col gap-[16px] bg-[#F1F4F9] rounded-[8px] lg:p-[16px] p-[12px]">                    
                        <h1 className="text-[#1A202C] text-[18px] leading-[130%] font-medium tracking-[-1%]">Recommended task</h1>
                        <div className="bg-[#FFFFFF] border-[#E6E6E6] border-[1px] px-[12px] py-[8px] rounded-[12px] flex lg:flex-row flex-col gap-[8px] lg:justify-between lg:items-center">
                            <div className="flex flex-row gap-[8px] py-[12px] items-center">
                                <Circle className="w-4 h-4 text-[#00C781]" />                   
                                <span className="text-[#003D26] text-[14px] leading-[140%] font-medium tracking-[-1%]">
                                    Complete pre-appointment questionnaire
                                </span>
                            </div>
                            <Button className="border-[#DCDCDE] h-auto lg:w-fit w-full text-[#FFFFFF] !py-[12px] !px-[16px] rounded-[10px]">
                                Complete now           
                            </Button>
                        </div>
                    </div>
                )} */}

                {/* Action buttons */}
                <div className="flex flex-row w-full gap-[12px]">
                    <Button 
                    onClick={() => handleSelectAppointment(appointment?.appointment_id ?? "")}
                    variant="outline" className="border-[#DCDEE3] h-auto w-full text-[#464B55] !py-[12px] !px-[16px] rounded-[10px]">
                        Reschedule            
                    </Button> 
                    {isUpcoming && (
                        <a href={appointment?.zoom_metadata.join_url}
                        target="_blank" rel="noopener noreferrer"
                        className={cn(buttonVariants({ variant: "default" }), "bg-[#4272DD] h-auto w-full !py-[12px] !px-[16px] rounded-[10px]", appointment.status !== "happening" ? "pointer-events-none cursor-not-allowed opacity-60 no-underline" : "")}>
                        Join meeting
                        </a>
                    )}
                </div>

                {/* What to do section */}
                <div className="bg-[#F1F4F9] rounded-[16px] p-[16px] flex flex-col gap-[16px]">
                    <h1 className="text-[#1A202C] text-[18px] leading-[130%] font-medium tracking-[-1%]">What to do</h1>
                    <div className="flex flex-col gap-[8px]">
                        <p className="text-[#464B55] text-[14px] leading-[140%] font-normal tracking-[-1%]">
                            This appointment will be conducted via video call. Please ensure you have:
                        </p>
                        <ol className="list-decimal list-inside flex flex-col gap-[8px] mt-2">
                            <li className="text-[#464B55] text-[14px] leading-[140%] font-normal tracking-[-1%]">
                                A stable internet connection
                            </li>
                            <li className="text-[#464B55] text-[14px] leading-[140%] font-normal tracking-[-1%]">
                                A quiet, private space for your appointment
                            </li>
                            <li className="text-[#464B55] text-[14px] leading-[140%] font-normal tracking-[-1%]">
                                Any relevant medical documents or information ready
                            </li>
                            <li className="text-[#464B55] text-[14px] leading-[140%] font-normal tracking-[-1%]">
                                A list of any questions or concerns you wish to discuss
                            </li>
                        </ol>
                        <span className="text-[#1A202C] text-[14px] leading-[140%] font-medium tracking-[-1%]">
                            Contact Support: Need help? Reach out to our support team appointments@proxymedicine.com
                        </span>
                    </div>
                </div>
            </div>
        </div>
        ):(
          <div className="border rounded-[12px] border-[#04040466] h-fit py-[20px]">
            <div className="flex flex-col gap-2 px-[20px]">
              <Skeleton className="h-[32px] w-[300px]" />
              <Skeleton className="h-[20px] w-[200px]" />
              <div className="flex flex-wrap items-center gap-[8px]">
                <Skeleton className="h-[24px] w-[100px]" />
                <Skeleton className="h-[24px] w-[100px]" />
                <Skeleton className="h-[24px] w-[100px]" />
              </div>
            </div>
            <div className="flex flex-col gap-[8px] mt-6">
              <div className="flex gap-2 border-b px-4 py-2">
                <Skeleton className="h-[40px] w-[100px]" />
                <Skeleton className="h-[40px] w-[100px]" />
                <Skeleton className="h-[40px] w-[100px]" />
                <Skeleton className="h-[40px] w-[100px]" />
              </div>
              <div className="mt-4 px-4">
                <Skeleton className="h-[200px] w-full" />
              </div>
            </div>
          </div>
        ) }
    </>
    )
}

// const AppointmentTimeTag = ({ time }: { time: string }) => {
//     const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
//     return (
//         <div className="flex items-center gap-2 bg-[#FFF6E7] text-[#FFA726] py-[3px] px-[10px] rounded-full text-[14px] leading-[140%] font-normal tracking-[-1%]">
//             {time === currentTime && <Video className="w-4 h-4" />}
//             {time !== currentTime && <Calendar className="w-4 h-4" />}
//             {time === currentTime ? 'Happening now' : time}
//         </div>
//     )
// }

const AppointmentStatusTag = ({ type }: { type: string }) => {
    const tagStyles = {
        "completed": "text-[#00C781] bg-[#E6FAF2]",
        "confirmed": "text-[#00C781] bg-[#E6FAF2]",
        "scheduled": "text-[#FFA726] bg-[#FFF6E7]",
        "missed": "text-[#7F8493] bg-[#7F849326]",
        "cancelled": "text-[#DC3545] bg-[#F8D7DA]"
    }[type] || "text-[#7F8493] bg-[#7F849326]";

    return (
        <span className={`${tagStyles} text-[14px] w-fit leading-[140%] font-medium flex items-center gap-[2px] py-[2px] px-[5px] rounded-full tracking-[-1%]`}>
            {type === "completed" && <CircleCheck className="size-4" />}
            {type === "confirmed" && <CircleCheck className="size-4" />}
            {type === "Missed" && <X className="size-4" />}
            {type === "cancelled" && <X className="size-4" />}
            {type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')}
        </span>
    )
}

const AppointmentReschedule = () => {
    return (
        <div className="flex flex-col gap-[32px] hidden">
            <div className="flex justify-end w-full">
                <button><CircleX className="w-8 h-8 text-[#7F8493]" /></button>
            </div>
            <h1 className="text-[#2D2F35] text-[32px] leading-[140%] font-medium tracking-[-2%] text-center">
                Reschedule appointment
            </h1>
            <div className="grid grid-cols-6 py-[16px] gap-[16px]">
                <div style={{boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.05)"}} className="flex flex-col col-span-2 gap-[16px] border-[#E6E6E6] border-[1px] rounded-[16px] py-[16px]">
                    <div className="border-b border-[#E6E6E6] pb-[16px]">
                        <h1 className="text-[#1A202C] text-[20px] leading-[150%] font-medium tracking-[-2%] text-center px-[20px]">
                            Weight loss consultation
                        </h1>
                    </div> 
                    <div className="border-b border-[#E6E6E6] pb-[16px]">
                        <p className="text-[#2D2F35] text-[18px] leading-[130%] font-medium tracking-[-2%] text-center px-[20px]">
                            Current Booking Details
                        </p>
                    </div> 
                    <div className="flex flex-col gap-[16px]">
                        <div className="flex gap-[8px] px-[20px] items-center">
                            <UserRound className="w-6 h-6 text-[#7F8493]" /> 
                            <span className="text-[#26282F] text-[16px] leading-[150%] font-normal tracking-[0%]">
                                Maurice Umoh
                            </span>
                        </div>                        
                        <div className="flex gap-[8px] px-[20px] items-center">
                            <Calendar className="w-6 h-6 text-[#7F8493]" /> 
                            <span className="text-[#26282F] text-[16px] leading-[150%] font-normal tracking-[0%]">
                                Wednesday, March 26, 2025
                            </span>
                        </div>                        
                        <div className="flex gap-[8px] px-[20px] items-center">
                            <Bell className="w-6 h-6 text-[#7F8493]" /> 
                            <span className="text-[#26282F] text-[16px] leading-[150%] font-normal tracking-[0%]">
                                12:00 AM, West africa
                            </span>
                        </div>
                        <div className="flex gap-[8px] px-[20px] items-center">
                            <Clock className="w-6 h-6 text-[#7F8493]" /> 
                            <span className="text-[#26282F] text-[16px] leading-[150%] font-normal tracking-[0%]">
                                60 minutes
                            </span>
                        </div>                        
                    </div>
                </div>
                <div style={{boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.05)"}} className="col-span-4 border-[#E6E6E6] border-[1px] rounded-[16px] p-[16px]">
                    {/* Reschedule calendar/form content would go here */}
                </div>
            </div>
        </div>
    )
}